import React, { useState, useEffect } from 'react';
import { useDatabase } from '../../db/DatabaseProvider';
import { useBranches, usePaymentMethods, useBaseCurrency, useActiveRate } from '../../db/hooks/useLookupData';
import { useAuthStore, usePOSStore } from '../../stores';
import { useToast } from '../common/ToastProvider';
import { generateId, now } from '../../lib/uuid';
import {
  Package,
  Plus,
  CheckCircle2,
  Calendar,
  Clock,
  User,
  Sparkles,
} from 'lucide-react';
import { Button } from '../common/Button';
import { CustomSelect } from '../common/CustomSelect';
import { Modal } from '../common/Modal';
import type {
  CustomerPackageDocType,
  CustomerPackageDetailType,
  CustomerAttentionType,
  PackageDocType,
  ServiceDocType,
  StaffDocType,
} from '../../db/schemas';

interface Props {
  customerId: string;
  customerName: string;
}

export function CustomerPackagesManager({ customerId, customerName }: Props) {
  const db = useDatabase();
  const { branches = [] } = useBranches();
  const toast = useToast();
  const { paymentMethods: methods = [] } = usePaymentMethods();
  const { currencySymbol, baseCurrency } = useBaseCurrency();
  const { activeRate } = useActiveRate();
  const selectedBranchId = useAuthStore((s) => s.selectedBranchId);

  // RxDB Reactive Data
  const [customerPackages, setCustomerPackages] = useState<CustomerPackageDocType[]>([]);
  const [packagesCatalog, setPackagesCatalog] = useState<PackageDocType[]>([]);
  const [services, setServices] = useState<ServiceDocType[]>([]);
  const [staff, setPersonal] = useState<StaffDocType[]>([]);

  // POS Store integration
  const openPOS = usePOSStore((s) => s.open);
  const setIsSelectingService = usePOSStore((s) => s.setIsSelectingService);

  // Modals state
  const [isAtencionModalOpen, setIsAtencionModalOpen] = useState(false);
  const [selectedPaqueteForAtencion, setSelectedPaqueteForAtencion] = useState<CustomerPackageDocType | null>(null);
  const [selectedDetalleForAtencion, setSelectedDetalleForAtencion] = useState<CustomerPackageDetailType | null>(null);

  // Atencion Form State
  const [selectedPersonalId, setSelectedPersonalId] = useState<string>('');
  const [atencionSucursalId, setAtencionSucursalId] = useState<string>('');
  const [notasAtencion, setNotasAtencion] = useState<string>('');

  // Subscribe to RxDB collections
  useEffect(() => {
    if (!db || !customerId) return;

    const subPackages = db.customer_packages
      .find({ selector: { customer_id: customerId, is_deleted: false } })
      .$.subscribe((docs) => {
        setCustomerPackages(docs.map((d) => d.toJSON()));
      });

    const subCatalogPackages = db.packages
      .find()
      .$.subscribe((docs) => {
        setPackagesCatalog(docs.map((d) => d.toJSON()).filter((p) => !p.is_deleted && p.is_active !== false));
      });

    const subServices = db.services
      .find({ selector: { is_deleted: false } })
      .$.subscribe((docs) => {
        setServices(docs.map((d) => d.toJSON()));
      });

    const subPersonal = db.staff
      .find({ selector: { is_deleted: false, is_active: true } })
      .$.subscribe((docs) => {
        setPersonal(docs.map((d) => d.toJSON()));
      });

    return () => {
      subPackages.unsubscribe();
      subCatalogPackages.unsubscribe();
      subServices.unsubscribe();
      subPersonal.unsubscribe();
    };
  }, [db, customerId]);

  // Open POS with client selected & package catalog active
  const handleOpenPOS = async () => {
    if (!db || !customerId) return;
    const clientDoc = await db.customers.findOne(customerId).exec();
    openPOS(clientDoc ? clientDoc.toJSON() : null);
    setIsSelectingService(true);
  };

  // Open Redemption / Atención Modal for a specific service detail credit
  const openAtencionModal = (cp: CustomerPackageDocType, det: CustomerPackageDetailType) => {
    setSelectedPaqueteForAtencion(cp);
    setSelectedDetalleForAtencion(det);
    setSelectedPersonalId(staff[0]?.id || '');
    setAtencionSucursalId(branches[0]?.id || '');
    setNotasAtencion('');
    setIsAtencionModalOpen(true);
  };

  const [isSavingAtencion, setIsSavingAtencion] = useState(false);

  // Confirm Atención / Session Redemption
  const handleConfirmAtencion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!db || !selectedPaqueteForAtencion || !selectedDetalleForAtencion || !customerId || isSavingAtencion) return;

    if (!selectedPersonalId) {
      toast.warning('Debes seleccionar un esteticista / operador asignado para la atención.', 'Operador Requerido');
      return;
    }

    setIsSavingAtencion(true);

    try {
      const cpDoc = await db.customer_packages.findOne({ selector: { id: selectedPaqueteForAtencion.id } }).exec();
      if (!cpDoc) return;

      const currentCp = cpDoc.toJSON();
      
      // Find latest detail state inside current doc revision
      const detailsList = currentCp.details || [];
      const targetDetail = detailsList.find((d: any) => d.id === selectedDetalleForAtencion.id);
      if (!targetDetail || targetDetail.remaining_sessions <= 0) {
        toast.warning('No quedan sesiones disponibles para este servicio en el paquete.', 'Sesiones Agotadas');
        return;
      }

      const timestamp = now();
      const serv = services.find((s) => s.id === targetDetail.service_id);
      const basePrice = serv?.currency_price || 0;
      const commissionPct = serv?.commission_percentage || 0;
      const commissionAmount = Math.round(((basePrice * commissionPct) / 100 + Number.EPSILON) * 100) / 100;

      const newAtencion: CustomerAttentionType = {
        id: generateId('atn'),
        service_id: targetDetail.service_id,
        staff_id: selectedPersonalId,
        branch_id: atencionSucursalId || selectedBranchId || branches[0]?.id || '',
        attention_date: timestamp,
        status: 'completed',
        commission_amount: commissionAmount,
        notes: notasAtencion.trim() || null,
      };

      // Update details array
      const updatedDetails = detailsList.map((d: any) => {
        if (d.id === targetDetail.id) {
          const newUsadas = d.used_sessions + 1;
          const newRestantes = d.total_sessions - newUsadas;
          return { ...d, used_sessions: newUsadas, remaining_sessions: newRestantes };
        }
        return d;
      });

      const updatedAtenciones = [...(currentCp.attentions || []), newAtencion];
      const allDepleted = updatedDetails.every((d: any) => d.remaining_sessions <= 0);

      await cpDoc.patch({
        details: updatedDetails,
        attentions: updatedAtenciones,
        status: allDepleted ? 'exhausted' : currentCp.status,
        updated_at: timestamp,
      });

      setIsAtencionModalOpen(false);
      toast.success('¡Atención registrada correctamente! Se descontó 1 sesión y se asignó la comisión al esteticista.', 'Atención Registrada');
    } catch (err: any) {
      toast.error('Error al registrar la atención: ' + err.message);
    } finally {
      setIsSavingAtencion(false);
    }
  };



  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header and Action Button */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-surface-secondary backdrop-blur-md border border-border p-5 rounded-2xl shadow-xs glass-card">
        <div>
          <div className="flex items-center gap-2 text-brand-orange font-semibold text-xs uppercase tracking-wider">
            <Package className="w-4 h-4" /> Control de Atenciones & Créditos
          </div>
          <h3 className="text-lg font-bold text-text-primary mt-1">
            Paquetes Adquiridos por {customerName}
          </h3>
          <p className="text-xs text-text-secondary mt-0.5">
            1 Pago único otorga N attentions en catálogo para redimir en citas.
          </p>
        </div>

        <Button onClick={handleOpenPOS} size="sm" icon={Plus} className="self-start sm:self-auto">
          Vender / Asignar Paquete
        </Button>
      </div>

      {/* Active Packages List */}
      <div className="space-y-4">
        {customerPackages.map((cp) => {
          const detalles = cp.details || [];
          const attentions = cp.attentions || [];
          const totalSessions = detalles.reduce((acc: number, d: any) => acc + d.total_sessions, 0);
          const usedSessions = detalles.reduce((acc: number, d: any) => acc + d.used_sessions, 0);
          const pctAvance = totalSessions > 0 ? Math.round((usedSessions / totalSessions) * 100) : 0;

          return (
            <div
              key={cp.id}
              className="bg-surface border border-border/80 rounded-2xl p-5 shadow-xs space-y-4 hover:border-brand-sky/40 transition-all"
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-border/60 pb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-extrabold text-base text-text-primary">{cp.package_name}</h4>
                    <span
                      className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full border ${
                        cp.status === 'active'
                          ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20'
                          : 'bg-surface-tertiary text-text-tertiary border-border'
                      }`}
                    >
                      {cp.status}
                    </span>
                  </div>
                  <span className="text-[11px] text-text-secondary flex items-center gap-1 mt-1">
                    <Calendar className="w-3.5 h-3.5 text-brand-orange" /> Adquirido el{' '}
                    {new Date(cp.purchase_date).toLocaleDateString('es-ES')}
                  </span>
                </div>

                {/* Progress bar */}
                <div className="w-full sm:w-56 bg-surface-secondary border border-border/60 p-2.5 rounded-xl shrink-0">
                  <div className="flex justify-between items-center text-[10px] font-bold text-text-secondary mb-1">
                    <span>Avance Atenciones</span>
                    <span>
                      {usedSessions} / {totalSessions} ({pctAvance}%)
                    </span>
                  </div>
                  <div className="w-full bg-surface-tertiary h-2 rounded-full overflow-hidden border border-border/30">
                    <div
                      className="bg-brand-orange h-full rounded-full transition-all duration-500"
                      style={{ width: `${pctAvance}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Service Credit Balances */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {detalles.map((det: any) => {
                  const serv = services.find((s) => s.id === det.service_id);
                  const disponible = det.remaining_sessions > 0;

                  return (
                    <div
                      key={det.id}
                      className="bg-surface-secondary/70 border border-border/40 p-4 rounded-xl flex flex-col justify-between hover:bg-surface-secondary transition-all"
                    >
                      <div className="space-y-2">
                        <span className="font-extrabold text-xs text-text-primary block leading-tight">
                          {serv ? serv.concept : 'Servicio'}
                        </span>
                        <div className="flex items-center justify-between text-xs pt-1 border-t border-border/40">
                          <span className="text-text-secondary text-[11px] font-bold">Disponibles:</span>
                          <span className="font-black text-brand-orange text-sm px-2 py-0.5 bg-brand-orange/10 rounded-md border border-brand-orange/20">
                            {det.remaining_sessions} / {det.total_sessions}
                          </span>
                        </div>
                      </div>

                      {disponible && cp.status === 'active' && (
                        <div className="mt-3 pt-2">
                          <Button
                            variant="sky"
                            size="xs"
                            fullWidth
                            icon={CheckCircle2}
                            onClick={() => openAtencionModal(cp, det)}
                            className="text-xs font-extrabold py-2 text-wrap"
                          >
                            Registrar Atención
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Attentions History per Package */}
              {attentions.length > 0 && (
                <div className="mt-3 pt-3 border-t border-border/60 space-y-2">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-text-tertiary flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5 text-brand-orange" /> Histórico de Atenciones ({attentions.length})
                  </span>
                  <div className="divide-y divide-border/40 max-h-44 overflow-y-auto custom-scrollbar">
                    {attentions.map((atn: any) => {
                      const serv = services.find((s) => s.id === atn.service_id);
                      const staffId = atn.staff_id;
                      const op = staff.find((p) => p.id === staffId);
                      const dateStr = atn.attention_date || '';
                      const commAmt = atn.commission_amount ?? 0;

                      return (
                        <div key={atn.id} className="py-2 flex items-center justify-between text-xs">
                          <div>
                            <span className="font-bold text-text-primary">{serv?.concept || 'Atención'}</span>
                            <span className="text-text-secondary text-[11px] block mt-0.5">
                              Operador: {op?.name || 'No asignado'} • {dateStr ? new Date(dateStr).toLocaleDateString('es-ES') : ''}
                            </span>
                          </div>
                          <div className="text-right">
                            <span className="text-emerald-600 dark:text-emerald-400 font-bold block bg-emerald-500/10 px-2 py-0.5 rounded-md border border-emerald-500/20">
                              Comisión: {currencySymbol}{commAmt.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {customerPackages.length === 0 && (
          <div className="p-8 text-center bg-surface backdrop-blur-md rounded-2xl border border-dashed border-border shadow-xs">
            <Package className="w-8 h-8 mx-auto text-text-tertiary mb-2" />
            <p className="text-xs text-text-secondary font-medium">
              El cliente no posee packages ni attentions acumuladas.
            </p>
          </div>
        )}
      </div>

      {/* ── Modal: Registrar Atención (Redimir Sesión) ──────────────────── */}
      <Modal
        isOpen={isAtencionModalOpen}
        onClose={() => setIsAtencionModalOpen(false)}
        title="Registrar Atención de Servicio"
        subtitle="Descuenta 1 sesión del crédito disponible y calcula la comisión del operador"
        icon={CheckCircle2}
        maxWidth="md"
        zIndexClass="z-[9999]"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setIsAtencionModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="submit"
              form="atencion-form"
            >
              Descontar Sesión & Registrar
            </Button>
          </>
        }
      >
        {selectedDetalleForAtencion && (
          <form id="atencion-form" onSubmit={handleConfirmAtencion} className="space-y-4">
            <div className="bg-brand-orange/10 border border-brand-orange/20 p-4 rounded-xl">
              <span className="text-[11px] text-text-secondary block font-semibold">Servicio a Redimir:</span>
              <span className="font-extrabold text-sm text-brand-orange block mt-0.5">
                {services.find((s) => s.id === selectedDetalleForAtencion.service_id)?.concept}
              </span>
              <span className="text-[11px] text-text-secondary block mt-1.5">
                Sesiones Restantes antes de esta atención:{' '}
                <strong className="text-text-primary">{selectedDetalleForAtencion.remaining_sessions}</strong>
              </span>
            </div>

            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1">
                Esteticista / Operador Asignado *
              </label>
              <CustomSelect
                value={selectedPersonalId}
                onChange={setSelectedPersonalId}
                options={staff.map((p) => ({
                  value: p.id,
                  label: `${p.name} (${p.role || 'Staff'})`,
                }))}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1">Sucursal de Atención *</label>
              <CustomSelect
                value={atencionSucursalId}
                onChange={setAtencionSucursalId}
                options={branches.map((s) => ({
                  value: s.id,
                  label: s.name,
                }))}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1">Notas de la Atención</label>
              <input
                type="text"
                value={notasAtencion}
                onChange={(e) => setNotasAtencion(e.target.value)}
                placeholder="Ej: Zona tratada, potencia o parámetros usados..."
                className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-xs text-text-primary focus:border-brand-sky focus:ring-2 focus:ring-brand-sky/20 transition-all shadow-xs"
              />
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
